import React from "react";
import { connect } from "react-redux";
import { Col, Row, Button, Overlay, Popover } from "react-bootstrap";
import PropTypes from "prop-types";
import Select from "react-select";

import { withNavigation } from "../../../utils/wrapper";
import Table from "../../../layout/table";
import Hamburger from "../../../layout/kebab";
import { activity } from "../../../actions/invoices";
import { format as formatDate } from "../../../utils/date";
import { currency } from "../../../utils/number";
import { isGuid } from "../../../utils/string";
import CsLineIcons from "../../../cs-line-icons";

class Activities extends React.Component {
    sizeOptions = [
        { value: 10, label: "10" },
        { value: 25, label: "25" },
        { value: 50, label: "50" },
        { value: 100, label: "100" },
    ];

    get sizeValue() {
        return this.sizeOptions.find((o) => o.value === Number(this.state.size)) || this.sizeOptions[0];
    }

    selectStyles = {
        container: (base) => ({ ...base, minWidth: 86 }),
        control: (base) => ({
            ...base,
            minHeight: 26,
            height: 26,
            borderColor: "transparent",
            boxShadow: "none",
            backgroundColor: "transparent",
        }),
        valueContainer: (base) => ({ ...base, padding: "0 4px", height: 26 }),
        input: (base) => ({ ...base, margin: 0, padding: 0 }),
        indicatorsContainer: (base) => ({ ...base, height: 26 }),
        dropdownIndicator: (base) => ({ ...base, padding: 2 }),
        indicatorSeparator: () => ({ display: "none" }),
        singleValue: (base) => ({ ...base, fontSize: 12, color: "#6c757d" }),
        menu: (base) => ({ ...base, zIndex: 20 }),
    };

    constructor(props) {
        super(props);
        this.state = {
            page: 0,
            size: 10,
            data: [],
            count: 0,
            pages: 0,
            loading: false,
            menu: {
                show: false,
                row: null,
                target: null,
            },
        };
        this.loading = false;
    }

    componentDidMount = async () => {
        await this.loadData();
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.person !== this.props.person) {
            this.setState(
                {
                    page: 0,
                    data: [],
                    count: 0,
                    pages: 0,
                },
                this.loadData
            );
        }
    }

    loadData = async () => {
        if (this.loading) return;
        this.loading = true;
        this.setState({ loading: true });

        try {
            const response = await this.props.activity({
                page: this.state.page,
                size: this.state.size,
                personUuid: this.props.person,
            });

            const items = Array.isArray(response?.items) ? response.items : [];
            const count = Number(response?.count ?? 0);
            const pages = items.length > 0 ? Math.ceil(count / Number(this.state.size)) : 0;

            this.setState({
                data: items,
                count,
                pages,
                loading: false,
            });
        } catch (err) {
            console.error("Activities.loadData failed:", err);
            this.setState({
                data: [],
                count: 0,
                pages: 0,
                loading: false,
            });
        } finally {
            this.loading = false;
        }
    };

    onTypeFormat = (e, row) => {
        let label = "Unknown";

        switch (e) {
            case "PAYMENT":
                label = "Payment by " + (row.personLastName || "");
                break;
            case "CLOCK":
                label = "Time spent by " + (row.staffLastName || "");
                break;
            case "EXPENSE":
                label = "Expense by " + (row.staffLastName || "");
                break;
            case "INVOICE":
                label = "Invoice #" + (row.number || "");
                break;
            default:
                break;
        }

        return (
            <>
                <Button variant="link" className="p-0 align-baseline" onClick={() => this.onItemClick(row)}>
                    {label}
                </Button>

                {isGuid(row.invoiceUuid) && (
                    <button
                        type="button"
                        className="badge rounded-pill bg-primary"
                        style={{ marginLeft: 5 }}
                    >
                        Inv. #{row.invoiceNumber ?? "???"}
                    </button>
                )}

                {e === "INVOICE" && row.sent && (
                    <button
                        type="button"
                        className="badge rounded-pill bg-primary"
                        style={{ marginLeft: 5 }}
                    >
                        Sent {formatDate(row.sent)}
                    </button>
                )}

                {e === "INVOICE" && !row.sent && (
                    <button
                        type="button"
                        className="badge rounded-pill bg-primary"
                        style={{ marginLeft: 5 }}
                    >
                        Unsent
                    </button>
                )}

                {e === "INVOICE" && row.published && (
                    <button
                        type="button"
                        className="badge rounded-pill bg-primary"
                        style={{ marginLeft: 5 }}
                    >
                        Pub. {formatDate(row.published)}
                    </button>
                )}

                {e === "INVOICE" && !row.published && (
                    <button
                        type="button"
                        className="badge rounded-pill bg-primary"
                        style={{ marginLeft: 5 }}
                    >
                        Unpublished
                    </button>
                )}
            </>
        );
    };

    onDateFormat = (e, row) => (
        <Button variant="link" className="p-0 align-baseline" onClick={() => this.onItemClick(row)}>
            {formatDate(e, true)}
        </Button>
    );

    onMemoFormat = (e, row) => (
        <Button variant="link" className="p-0 align-baseline" onClick={() => this.onItemClick(row)}>
            {e}
        </Button>
    );

    onAmountFormat = (e, row) => {
        const amount = Math.abs(Number(e));
        return (
            <Button variant="link" className="p-0 align-baseline" onClick={() => this.onItemClick(row)}>
                {currency(amount)}
            </Button>
        );
    };

    onBalanceFormat = (e, row) => {
        const balance = Number(e);
        const formatted = currency(Math.abs(balance));
        return (
            <Button variant="link" className="p-0 align-baseline" onClick={() => this.onItemClick(row)}>
                {balance < 0 ? `-${formatted}` : formatted}
            </Button>
        );
    };

    onCaseFormat = (e, row) =>
        row.caseCaption ? (
            <>
                <Button variant="link" className="p-0 align-baseline" onClick={() => this.onItemClick(row)}>
                    {row.caseCaption} {row.caseNumber}
                </Button>
                <a href={`/dashboard/matters/${row.caseUuid}`}>
                    <CsLineIcons size="8" icon="arrow-bottom-left" />
                </a>
            </>
        ) : null;

    onItemClick = (row) => {
        if (this.props.onItemClick) this.props.onItemClick(row);
    };

    onItemDelete = (row) => {
        if (this.props.onDeleteClick) this.props.onDeleteClick(row);
        this.closeHamburger();
    };

    onPublish = (uuid) => {
        if (this.props.onPublish) this.props.onPublish(uuid);
        this.closeHamburger();
    };

    onSend = (uuid) => {
        if (this.props.onSend) this.props.onSend(uuid);
        this.closeHamburger();
    };

    openHamburger = (event, row) => {
        event.preventDefault();
        this.setState({
            menu: {
                show: true,
                row,
                target: event.currentTarget,
            },
        });
    };

    closeHamburger = () => {
        this.setState((prev) => ({
            menu: {
                ...prev.menu,
                show: false,
                row: null,
                target: null,
            },
        }));
    };

    onActionFormat = (e, row) => (
        <Hamburger variant="link" vertical={false} onClick={(evt) => this.openHamburger(evt, row)} />
    );

    onPageChange = (e) => {
        this.setState(
            {
                page: Number(e),
            },
            this.loadData
        );
    };

    onSize = (e) => {
        this.setState(
            {
                page: 0,
                size: Number(e),
            },
            this.loadData
        );
    };

    get headers() {
        return [
            {
                header: "Type",
                sortable: true,
                source: "type",
                className: "text-muted text-small text-uppercase w-20",
                onRender: this.onTypeFormat,
            },
            {
                header: "Date Of",
                sortable: false,
                source: "activityDate",
                className: "text-muted text-small text-uppercase text-end pr-2 w-20",
                dataClassName: "text-end",
                onRender: this.onDateFormat,
            },
            {
                header: "Notes",
                sortable: false,
                source: "memo",
                className: "text-muted text-small text-uppercase w-40",
                onRender: this.onMemoFormat,
            },
            {
                header: "In re",
                className: "text-muted text-small text-uppercase w-40",
                source: "caseCaption",
                sortable: false,
                onRender: this.onCaseFormat,
            },
            {
                header: "Amount",
                sortable: false,
                source: "signed",
                className: "text-muted text-small text-uppercase text-center w-10",
                dataClassName: "text-end",
                onRender: this.onAmountFormat,
            },
            {
                header: "Running",
                sortable: false,
                source: "balance",
                className: "text-muted text-small text-uppercase text-center w-10",
                dataClassName: "text-end",
                onRender: this.onBalanceFormat,
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                onRender: this.onActionFormat,
            },
        ];
    }

    get showSizeSelector() {
        return Number(this.state.count || this.state.data.length || 0) >= 10;
    }

    render() {
        const menuRow = this.state.menu?.row || {};

        return (
            <>
                <Row className="g-3">
                    <Col xs="12" className="d-flex align-items-center justify-content-between">
                        <h2 className="small-title mb-0">Financial Activity</h2>

                        {this.showSizeSelector ? (
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">Rows</span>
                            <Select
                                classNamePrefix="ra"
                                value={this.sizeValue}
                                options={this.sizeOptions}
                                onChange={(opt) => this.onSize(opt?.value)}
                                isSearchable={false}
                                styles={this.selectStyles}
                                aria-label="Rows per page"
                            />
                        </div>
                        ) : null}
                    </Col>

                    <Col xs="12">
                        <Table
                            headers={this.headers}
                            page={this.state.data}
                            size={this.state.size}
                            index={this.state.page}
                            pages={this.state.pages}
                            onPageChange={this.onPageChange}
                            searchable={false}
                            sortable={false}
                            sizable={false}
                        />
                    </Col>
                </Row>

                <Overlay
                    target={this.state.menu.target}
                    show={this.state.menu.show}
                    placement="bottom-end"
                    rootClose
                    onHide={this.closeHamburger}
                >
                    {(props) => (
                        <Popover {...props}>
                            <Popover.Body>
                                <Row>
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => this.onItemClick(menuRow)}
                                        >
                                            Open
                                        </button>
                                    </Col>
                                </Row>

                                {menuRow.type === "INVOICE" && !menuRow.published && (
                                    <Row>
                                        <Col className="d-flex flex-row mt-1">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 me-2 btn-sm small text-start"
                                                onClick={() => this.onPublish(menuRow.uuid)}
                                            >
                                                Publish
                                            </button>
                                        </Col>
                                    </Row>
                                )}

                                {menuRow.type === "INVOICE" && (
                                    <Row>
                                        <Col className="d-flex flex-row mt-1">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 me-2 btn-sm small text-start"
                                                onClick={() => this.onSend(menuRow.uuid)}
                                            >
                                                Send
                                            </button>
                                        </Col>
                                    </Row>
                                )}

                                {!isGuid(menuRow.invoiceUuid) && menuRow.uuid && (
                                    <Row>
                                        <Col className="d-flex flex-row mt-1">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 me-2 btn-sm small text-start"
                                                onClick={() => this.onItemDelete(menuRow)}
                                            >
                                                Delete
                                            </button>
                                        </Col>
                                    </Row>
                                )}
                            </Popover.Body>
                        </Popover>
                    )}
                </Overlay>
            </>
        );
    }
}

Activities.propTypes = {
    auth: PropTypes.object.isRequired,
    person: PropTypes.string,
    onItemClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    onPublish: PropTypes.func,
    onSend: PropTypes.func,
    activity: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default withNavigation(
    connect(
        mapStateToProps,
        {
            activity,
        },
        null,
        { forwardRef: true }
    )(Activities)
);
